import Mixpanel from 'mixpanel-browser';
import { MIXPANEL_TRACE_PROPERTIES } from '../constants';

class Adapter {
  constructor(dependencies) {
    const { env } = dependencies;

    this.mixPanelPlugIn = {
      enable: JSON.parse(env.MIXPANEL_ENABLED),
      mixpanel: null,
      token: JSON.parse(env.MIXPANEL_ENABLED)
        ? env.MIXPANEL_PROJECT_TOKEN
        : JSON.parse(env.MIXPANEL_PROJECT_TOKEN),
      isSetup: false
    };

    this.appcuesPlugin = {
      isEnabled: JSON.parse(env.APPCUES_ENABLED)
    };
  }

  // 1. Mixpanel
  initialPlugIn() {
    if (this.mixPanelPlugIn.enable) {
      Mixpanel.init(this.mixPanelPlugIn.token);
      this.mixPanelPlugIn.mixpanel = Mixpanel;
      this.mixPanelPlugIn.isSetup = true;
    }
  }

  mixPanelUpdateUserProfile(peopleArg) {
    const { email, name, userRole, idpUri } = peopleArg;

    if (this.mixPanelPlugIn.isSetup) {
      this.mixPanelPlugIn.mixpanel.identify(email);

      const mixpanelPeopleArg = {
        $email: email,
        $name: name,
        userRole,
        idp: idpUri
      };

      this.mixPanelPlugIn.mixpanel.people.set(mixpanelPeopleArg);
    }
  }

  static getCustomMixpanelProperties(properties) {
    const {
      appName,
      subdomain,
      userRole,
      moduleId,
      moduleOwnerId,
      moduleName,
      moduleCustomDisplayName,
      moduleSecure,
      moduleAccessGroup,
      pagePath,
      pageOwnerId,
      pageTitle,
      contextKeyName,
      quickLinkTitle,
      quickLinkType,
      quickLinkUrl,
      exportedModulePages,
      exportedPagePaths,
      exportedModuleTOCs,
      client,
      publicView,
      restoreArchivedPages,
      deleteUGCPages,
      publishId,
      publishSummary,
      publishAt
    } = properties;

    const result = {};

    if (appName !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.APP_NAME] = appName;
    }

    if (client && client.id !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.CLIENT_ID] = client.id;
    }

    if (client && client.universalId !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.UNIVERSAL_CLIENT_ID] = client.universalId;
    }

    if (client && client.displayName) {
      result[MIXPANEL_TRACE_PROPERTIES.CLIENT_NAME] = client.displayName;
    }

    if (subdomain) {
      result[MIXPANEL_TRACE_PROPERTIES.TENANT] = subdomain;
    }

    if (userRole !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.USER_ROLE] = userRole;
    }

    if (moduleId) {
      result[MIXPANEL_TRACE_PROPERTIES.MODULE_ID] = moduleId;
    }

    if (moduleOwnerId !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.MODULE_OWNER_ID] = moduleOwnerId;
    }

    if (moduleName !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.MODULE_NAME] = moduleName;
    }

    if (moduleCustomDisplayName !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.MODULE_CUSTOM_DISPLAY_NAME] = moduleCustomDisplayName;
    }

    if (moduleSecure !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.MODULE_SECURE] = moduleSecure;
    }

    if (moduleAccessGroup !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.MODULE_ACCESS_GROUP] = moduleAccessGroup;
    }

    if (pagePath) {
      result[MIXPANEL_TRACE_PROPERTIES.PAGE_PATH] = pagePath;
    }

    if (pageOwnerId !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.PAGE_OWNER_ID] = pageOwnerId;
    }

    if (pageTitle) {
      result[MIXPANEL_TRACE_PROPERTIES.PAGE_TITLE] = pageTitle;
    }

    if (publicView !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.PUBLIC_VIEW] = publicView;
    }

    if (contextKeyName) {
      result[MIXPANEL_TRACE_PROPERTIES.CONTEXT_KEY_NAME] = contextKeyName;
    }

    if (quickLinkTitle) {
      result[MIXPANEL_TRACE_PROPERTIES.QUICK_LINK_TITLE] = quickLinkTitle;
    }

    if (quickLinkType) {
      result[MIXPANEL_TRACE_PROPERTIES.QUICK_LINK_TYPE] = quickLinkType;
    }

    if (quickLinkUrl) {
      result[MIXPANEL_TRACE_PROPERTIES.QUICK_LINK_URL] = quickLinkUrl;
    }

    if (exportedModulePages) {
      result[MIXPANEL_TRACE_PROPERTIES.EXPORTED_MODULE_PAGES] = exportedModulePages;
    }

    if (exportedPagePaths) {
      result[MIXPANEL_TRACE_PROPERTIES.EXPORTED_PAGES] = exportedPagePaths;
    }

    if (exportedModuleTOCs) {
      result[MIXPANEL_TRACE_PROPERTIES.EXPORTED_MODULE_TOCS] = exportedModuleTOCs;
    }

    if (restoreArchivedPages) {
      result[MIXPANEL_TRACE_PROPERTIES.RESTORE_ARCHIVED_PAGE] = restoreArchivedPages;
    }

    if (deleteUGCPages) {
      result[MIXPANEL_TRACE_PROPERTIES.DELETE_UGC_PAGE] = deleteUGCPages;
    }

    if (publishId) {
      result[MIXPANEL_TRACE_PROPERTIES.PUBLISH_ID] = publishId;
    }
    if (publishSummary !== undefined) {
      result[MIXPANEL_TRACE_PROPERTIES.PUBLISH_SUMMARY] = publishSummary;
    }
    if (publishAt) {
      result[MIXPANEL_TRACE_PROPERTIES.PUBLISH_AT] = publishAt;
    }

    return result;
  }

  mixPanelTrackEvent(event, properties) {
    if (this.mixPanelPlugIn.isSetup) {
      this.mixPanelPlugIn.mixpanel.track(event, properties);
    }
  }

  // 2. Appcues
  appcuesIdentifyUser(userProperties = {}) {
    if (this.appcuesPlugin.isEnabled) {
      const { email, name, userRole, idpUri } = userProperties;
      const { Appcues } = window;

      if (!Appcues) throw new Error('Appcues is not found. Unable to identify Appcues user.');

      if (email) {
        // Using email as a unique identifier for each user.
        Appcues.identify(email, {
          email,
          name,
          userRole,
          idpUri
        });
      }
    }
  }

  appcuesTrackEvent(eventName, eventProperties) {
    if (this.appcuesPlugin.isEnabled && eventName) {
      const { Appcues } = window;

      if (Appcues) {
        if (eventProperties) {
          Appcues.track(eventName, eventProperties);
        } else {
          Appcues.track(eventName);
        }
      }
    }
  }
}

export default { Adapter };
